import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../../popup/stations/Station.scss';
import { ONEDAY, backgroundColor, windDirectionChange, SFColors } from '../../../../../store/common';
import { useStoreContext } from '../../../../../store/context';
import arrow from '../../../../../assets/images/arrow.png';
import moment from 'moment-timezone';
import { alertMessages, humidityColors, temperatureColors, precip, windGustColors, solarRadiationColors, vpdColors } from '../../../../../constants';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { getWeatherMinMax } from 'src/apis/getWeatherCalls';
import { useAddAlert } from 'src/store/actions';
import { Button, TableHead } from '@mui/material';
import { Grid } from 'src/shared/grid';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { ROUTE_PATHS } from 'src/routes';
import LaunchIcon from '@mui/icons-material/Launch';

type DailyTablePopUpProps = {
  id:any;
  stationMeta: any;
}

export const DailyTablePopUp: React.FC<DailyTablePopUpProps> = ({
  id, stationMeta
}) => {
  const [state, dispatch] = useStoreContext();
  const [modifiedContents, setModifiedContents] = useState<any[]>([]);
  const addAlert = useAddAlert(dispatch);
  const scrollRef = useRef<HTMLTableElement | HTMLDivElement | null>(null);

  const fillMissingDates = useCallback((data: any[], startTime: any, endTime: any)=>{
    const filledData:any[] = [];
    let currentTime = moment.utc(startTime);
    const endMoment = moment.utc(endTime);

    if(stationMeta?.time_zone_offset){
      while(currentTime<=endMoment){
        const currentHour = currentTime.toISOString(); 
        const existingData:any = data?.find(item=>item.summary_date === moment(currentHour).utc().format('YYYY-MM-DD'));
        if(existingData){
          filledData.push(existingData);
        }
        else{
          filledData.push({
            station_id:stationMeta.station_id,
            station_name: stationMeta.station_name,
            summary_date: moment(currentHour).utc().format('YYYY-MM-DD'),
            observation_type: moment()>currentTime? 'O':'F',
            temperature_min:null,
            temperature_max: null,
            relative_humidity_min: null,
            relative_humidity_max: null,
            wind_speed_min: null,
            wind_speed_max: null,
            peak_gust_speed_min: null,
            peak_gust_speed_max: null,
            peak_wind_gust_direction: null,
            peak_wind_gust_time: null,
            solar_radiation_max: null,
            daily_precipitation_total: null,  
            snow_flag: null,
            vapor_press_def_min: null,
            vapor_press_def_max: null,
          })
        }
        currentTime.add(1, 'day');
      }
    }
    return filledData;
  },[stationMeta]);

  const wxMinMaxCallback = useCallback(() => {
    (async () => {
      try {
        const currentMinus3 = moment(state.currentSliderDate - (3 * ONEDAY)).utc().format('YYYY-MM-DD');
        const currentPlus3 = moment(state.currentSliderDate + (3 * ONEDAY)).utc().format('YYYY-MM-DD');
        const varRequest = {
          stationIds: id.toString(),
          zoomLevel: 5,
          startDate: currentMinus3,
          endDate: currentPlus3,
          page:0,
					perPage: 100000,
        };
        const response: any = await getWeatherMinMax(varRequest, addAlert);
        if (response.data?.length>0) {
          const sortedArray = response.data.sort((a:any, b:any) => moment(a.summary_date).valueOf() - moment(b.observation_time).valueOf());
          const responseLastObj = sortedArray[sortedArray.length-1]?.summary_date;
          const forecastUsualEnd =moment().add(7,'days').utc().format('YYYY-MM-DD')
          
          let fillDates: any;
          if (moment(forecastUsualEnd) >= moment(currentPlus3)) {
            if (moment(responseLastObj) >= moment(currentPlus3)) {
              fillDates = fillMissingDates(response.data, currentMinus3, responseLastObj);
            }
            else {
              fillDates = fillMissingDates(response.data, currentMinus3, currentPlus3);
            }
          }
          else {
            fillDates = fillMissingDates(response.data, currentMinus3, forecastUsualEnd);
          }
          const convert_response = fillDates?.length>0 && fillDates.map((item: any) => {
            let tempMaxColors = backgroundColor(item.temperature_max===null? -2000: Number(item.temperature_max), temperatureColors);
            let tempMinColors = backgroundColor(item.temperature_min===null? -2000: Number(item.temperature_min), temperatureColors);
            let rhMaxColors = backgroundColor(item.relative_humidity_max===null? -2000: Number(item.relative_humidity_max), humidityColors);
            let rhMinColors = backgroundColor(item.relative_humidity_min===null? -2000:Number(item.relative_humidity_min), humidityColors);

            let wsColors = backgroundColor(item.wind_speed_max===null? -2000:Number(item.wind_speed_max), windGustColors);
            let precipColors = backgroundColor(item.daily_precipitation_total===null? -2000:Number(item.daily_precipitation_total), precip);
            let vpdMaxColors = backgroundColor(item.vapor_press_def_max===null? -2000: Number(item.vapor_press_def_max), vpdColors);
            let vpdMinColors = backgroundColor(item.vapor_press_def_min===null? -2000: Number(item.vapor_press_def_min), vpdColors);

            let gsColors = backgroundColor(item.peak_gust_speed_max===null? -2000:Number(item.peak_gust_speed_max), windGustColors);
            let srColors = backgroundColor(item.solar_radiation_max===null? -2000:Number(item.solar_radiation_max), solarRadiationColors);
            let sfColors = SFColors(item.snow_flag===null? 'null':item.snow_flag)
            return (
              {
                ...item,
                dateNum: Number(moment(item.summary_date)),
                tempMaxBackground: tempMaxColors.bg,
                tempMaxTextColor: tempMaxColors.text,
                tempMinBackground: tempMinColors.bg,
                tempMinTextColor: tempMinColors.text,
                rhMaxBackground: rhMaxColors.bg,
                rhMaxTextColor: rhMaxColors.text,
                rhMinBackground: rhMinColors.bg,
                rhMinTextColor: rhMinColors.text,
                wsTextColor: wsColors.text,
                wsBackground: wsColors.bg,
                precipBackground: precipColors.bg,
                precipTextColor: precipColors.text,
                gsBackground: gsColors.bg,
                gsTextColor: gsColors.text,
                srBackground: srColors.bg,
                srTextColor: srColors.text,
                sfBackground: sfColors.bg,
                sfTextColor: sfColors.text,
                vpdMaxBackground: vpdMaxColors.bg,
                vpdMaxTextColor: vpdMaxColors.text,
                vpdMinBackground: vpdMinColors.bg,
                vpdMinTextColor: vpdMinColors.text
              }
            )
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted)
        }
        else{
          const forecastUsualEnd =moment().add(7,'days').utc().format('YYYY-MM-DD')
          
          let fillDates: any;
          if (moment(forecastUsualEnd) >= moment(currentPlus3)) {

            fillDates = fillMissingDates(response.data, currentMinus3, currentPlus3);
            
          }
          else {
            fillDates = fillMissingDates(response.data, currentMinus3, forecastUsualEnd);
          }
          const convert_response = fillDates?.length>0 && fillDates.map((item: any) => {
            let tempMaxColors = backgroundColor(item.temperature_max===null? -2000: Number(item.temperature_max), temperatureColors);
            let tempMinColors = backgroundColor(item.temperature_min===null? -2000: Number(item.temperature_min), temperatureColors);
            let rhMaxColors = backgroundColor(item.relative_humidity_max===null? -2000: Number(item.relative_humidity_max), humidityColors);
            let rhMinColors = backgroundColor(item.relative_humidity_min===null? -2000:Number(item.relative_humidity_min), humidityColors);

            let wsColors = backgroundColor(item.wind_speed_max===null? -2000:Number(item.wind_speed_max), windGustColors);
            let precipColors = backgroundColor(item.daily_precipitation_total===null? -2000:Number(item.daily_precipitation_total), precip);
            let vpdMaxColors = backgroundColor(item.vapor_press_def_max===null? -2000: Number(item.vapor_press_def_max), vpdColors);
            let vpdMinColors = backgroundColor(item.vapor_press_def_min===null? -2000: Number(item.vapor_press_def_min), vpdColors);

            let gsColors = backgroundColor(item.peak_gust_speed_max===null? -2000:Number(item.peak_gust_speed_max), windGustColors);
            let srColors = backgroundColor(item.solar_radiation_max===null? -2000:Number(item.solar_radiation_max), solarRadiationColors);
            let sfColors = SFColors(item.snow_flag===null? 'null':item.snow_flag)
            return (
              {
                ...item,
                dateNum: Number(moment(item.summary_date)),
                tempMaxBackground: tempMaxColors.bg,
                tempMaxTextColor: tempMaxColors.text,
                tempMinBackground: tempMinColors.bg,
                tempMinTextColor: tempMinColors.text,
                rhMaxBackground: rhMaxColors.bg,
                rhMaxTextColor: rhMaxColors.text,
                rhMinBackground: rhMinColors.bg,
                rhMinTextColor: rhMinColors.text,
                wsTextColor: wsColors.text,
                wsBackground: wsColors.bg,
                precipBackground: precipColors.bg,
                precipTextColor: precipColors.text,
                gsBackground: gsColors.bg,
                gsTextColor: gsColors.text,
                srBackground: srColors.bg,
                srTextColor: srColors.text,
                sfBackground: sfColors.bg,
                sfTextColor: sfColors.text,
                vpdMaxBackground: vpdMaxColors.bg,
                vpdMaxTextColor: vpdMaxColors.text,
                vpdMinBackground: vpdMinColors.bg,
                vpdMinTextColor: vpdMinColors.text
              }
            )
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted)
        }
      }
      catch (error) {
        console.log('error', error)
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        })
      }
    })();
  }, [addAlert, state.currentSliderDate, id, fillMissingDates]);

  useEffect(() => {
    if(stationMeta?.time_zone_offset){
      wxMinMaxCallback();
    }
  }, [wxMinMaxCallback, stationMeta?.time_zone_offset]);

  useEffect(() => {
    requestAnimationFrame(scrollTocurrentDate);
  }, [modifiedContents]);

  const scrollTocurrentDate = () =>{
    const element = document.getElementById('currentDate') as HTMLElement | null;
    const container = scrollRef.current;

    if(!element || !container) return;
    const elementRight = element.offsetLeft + element.offsetWidth;
    const containerWidth = container.offsetWidth;
    const left = elementRight - containerWidth;

    container.scrollTo({
      left,
      behavior: "smooth"
    });
  };
    const goToTable = () =>{
      window.open(`${ROUTE_PATHS.table}/weather/${stationMeta.station_id}/daily`, '_blank')
    };

    return (
        <div>
            <Grid row gap="md">
              <Grid desktop={{ col: 8 }} style={{ paddingBottom: '5px' }}>
                <Grid
                    desktop={{ col: 12 }} style={{ paddingBottom: '5px' }}>
                    <Button
                        variant="contained"
                        style={{ fontSize: '12px', width: '100%' }}
                        onClick={() => scrollTocurrentDate()}
                    >
                        <KeyboardDoubleArrowLeftIcon /> Scroll to Current Observation <KeyboardDoubleArrowRightIcon />
                    </Button>
                </Grid>
              </Grid>
              <Grid desktop={{ col: 4 }} style={{ paddingBottom: '5px' }}>
                <Button onClick={goToTable} style={{ textTransform: 'capitalize' }}>Go To Table <LaunchIcon fontSize='small' /></Button>
              </Grid>
            </Grid>
            <TableContainer ref={scrollRef} style={{ maxHeight: 450, maxWidth: 450, overflowX: "auto", overflowY: 'hidden' }} className="smooth-scrollbar">
                <Table className='table-popup' style={{ tableLayout: "auto" }} >
                    {/*header date*/}
                    {/*IMPORTANT: these table rows are actually columns- updated in Stations.scss*/}
                    <TableHead style={{ height: '0px', position: 'sticky', zIndex: 100, left: 0 }}>

                        {/* <TableRow> //style={{ height: '15px', width: '75px', position: 'sticky', zIndex: 100, left: 0 }}> */}
                        <TableRow>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '32px', position: 'sticky', zIndex: 50, top: 0, backgroundColor: '#A1A1A1', width: '150px' }}>DATE (LST)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX TEMP (°F)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MIN TEMP (°F)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX RH (%)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MIN RH (%)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX WIND (MPH)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '50px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX WIND GUST (TIME/DIR)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX WIND GUST (MPH)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>PRECIP TOTAL (IN)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX SR (W/m2)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MAX VPD (Pa)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>MIN VPD (Pa)</TableCell>
                            <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '32px', backgroundColor: '#A1A1A1', width: '150px' }}>SNOW FLAG</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow style={{ width: '100px' }} />
                        {modifiedContents?.length > 1 &&
                            modifiedContents.map((item: any, y) => (
                                <TableRow style={{ width: '100px' }}
                                    key={`${item.summary_date}-${y}`}
                                    id={item.summary_date=== (moment(state.currentSliderDate).format('YYYY-MM-DD'))?'currentDate':''}
                                    className={item.summary_date === (moment(state.currentSliderDate).format('YYYY-MM-DD')) ? (item.observation_type === 'F' ? 'selected-popup-dashed' : 'selected-popup') : ''}
                                >
                                    <TableCell style={{ fontSize: '12px', height: '30px', position: 'sticky', zIndex: 50, top: 0, backgroundColor: 'white', width: '100px' }}
                                        className={item.observation_type === 'F' ? 'forecast' : 'obs'}
                                        key={item.userTZ_display_hour_datetime}>{`${moment(item.summary_date).format('MM/DD/YY')} ${stationMeta?.time_zone}`}</TableCell>

                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`maxtemp-${item.temperature_max}-&t${y}`} style={item.tempMaxBackground ? { height: '30px', backgroundColor: item.tempMaxBackground, color: item.tempMaxTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.temperature_max === null ? "●●●" : item.temperature_max} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`tempmin-${item.temperature_min}-&m${y}`} style={item.tempMinBackground ? { height: '30px', backgroundColor: item.tempMinBackground, color: item.tempMinTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.temperature_min === null ? "●●●" : item.temperature_min} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`rhmax-${item.relative_humidity_max}-&maxrh${y}`} style={item.rhMaxBackground ? { height: '30px', backgroundColor: item.rhMaxBackground, color: item.rhMaxTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.relative_humidity_max === null ? "●●●" : item.relative_humidity_max} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`rhmin-${item.relative_humidity_min}-&minrh${y}`} style={item.rhMinBackground ? { height: '30px', backgroundColor: item.rhMinBackground, color: item.rhMinTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.relative_humidity_min === null ? "●●●" : item.relative_humidity_min} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`maxwind-${item.wind_speed_max}-&maxwind-${y}`} style={item.wsBackground ? { height: '30px', backgroundColor: item.wsBackground, color: item.wsTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.wind_speed_max === null ? "●●●" : item.wind_speed_max} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`maxgd-${item.peak_wind_gust_direction}-&maxgd-${y}`} style={{ height: '50px', width: '100px' }}>{item.peak_wind_gust_time === null ? "●●●" : `${item.peak_wind_gust_time}:00 `}{item.peak_wind_gust_direction === null ? "" : <img src={arrow} style={{ width: '15px', transform: 'rotate(' + windDirectionChange(item.peak_wind_gust_direction+270) + 'deg)' }} alt={`${item.peak_wind_gust_direction} at ${item.summary_date}`} />} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`maxgs-${item.peak_gust_speed_max}-&maxgs-${y}`} style={item.gsBackground ? { height: '30px', backgroundColor: item.gsBackground, color: item.gsTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.peak_gust_speed_max === null ? "●●●" : item.peak_gust_speed_max}</TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`pmax-${item.hourly_precip}&p-${y}`} style={item.precipBackground ? { height: '30px', backgroundColor: item.precipBackground, color: item.precipTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.daily_precipitation_total === null ? "●●●" : item.daily_precipitation_total} </TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`srmax-${item.solar_radiation_max}-&sr-${y}`} style={item.srBackground ? { height: '30px', backgroundColor: item.srBackground, color: item.srTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.solar_radiation_max === null ? "●●●" : item.solar_radiation_max}</TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`vpdmax-${item.vapor_press_def_max}-&vpd-${y}`} style={item.srBackground ? { height: '30px', backgroundColor: item.vpdMaxBackground, color: item.vpdMaxTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.vapor_press_def_max === null ? "●●●" : item.vapor_press_def_max}</TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`vpdmin-${item.vapor_press_def_min}-&vpd-${y}`} style={item.srBackground ? { height: '30px', backgroundColor: item.vpdMinBackground, color: item.vpdMinTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.vapor_press_def_min === null ? "●●●" : item.vapor_press_def_min}</TableCell>
                                    <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`sfmax-${item.snow_flag}-&sf-${y}`} style={item.sfBackground ? { height: '30px', backgroundColor: item.sfBackground, color: item.sfTextColor, width: '100px' } : { height: '30px', width: '100px' }}>{item.snow_flag === null ? "●●●" : item.snow_flag}</TableCell>

                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </div>

    )
}